import React, {createContext, useCallback, useContext, useEffect, useMemo, useState,} from "react";
import api from "../../services/api";
import {resolveEnumUrl} from "./enumHelper";
import {
    clearEnumByKeyAndLocaleFromDb,
    clearEnumsByLocaleFromDb,
    loadEnumsToMemoryFromDb,
    setEnumToDb,
} from "./db/db_manager";
import {enumMemoryCache} from "./enumMemoryCache";
import {ENUM_HASH_KEY, EnumContextType} from "./interface";
import {LocaleType} from "../../core/localization/intertace";
import {useTranslation} from "../../i18n/I18nProvider";
import {isBlank} from "../../helpers/stringHelper";
import {enumRequestManager} from "./requestManager";


const EnumContext = createContext<EnumContextType | undefined>(undefined);

export const EnumProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const {lhash, tlocale} = useTranslation();
    const [updateId, setUpdateId] = useState(0);

    const syncMemoryFromIndexedDB = useCallback(async (locale: LocaleType) => {
        await loadEnumsToMemoryFromDb(locale);
        setUpdateId((prev) => prev + 1);
    }, []);

    const invalidateIfHashChanged = useCallback(
        async (currentHash: string, tlocale: LocaleType) => {
            const currentLang = tlocale;
            const prevLang = localStorage.getItem("prev_enum_lang");
            if (prevLang === currentLang) return;
            localStorage.setItem("prev_enum_lang", currentLang);
            const prevHash = localStorage.getItem(ENUM_HASH_KEY(currentLang));
            if (!isBlank(currentHash) && currentHash.indexOf("-") < 0 && (currentHash !== prevHash)) {
                localStorage.setItem(ENUM_HASH_KEY(currentLang), currentHash);
                await clearEnumsByLocaleFromDb(currentLang); // tüm locale’leri temizle
                enumMemoryCache.clearAll();
                enumRequestManager.clearAll();
                setUpdateId((prev) => prev + 1);
            } else {
                enumMemoryCache.clearAll();
                enumRequestManager.clearAll();
                await syncMemoryFromIndexedDB(currentLang);
            }


        },
        [syncMemoryFromIndexedDB]
    );

    useEffect(() => {
        invalidateIfHashChanged(lhash, tlocale);
    }, [tlocale, lhash]);


    const getEnum = useCallback(
        async (key: string, url?: string): Promise<any[]> => {
            if (!key) return [];
            const cached = enumMemoryCache.get(key);
            if (cached) return cached;
            if (enumRequestManager.has(key)) {
                return enumRequestManager.get(key) ?? Promise.prototype;
            }
            const finalUrl = url ?? resolveEnumUrl(key);
            if (!finalUrl) return [];
            const promise = api.get(finalUrl)
                .then((response) => {
                    const data = response.data?.data || [];
                    enumMemoryCache.set(key, data);
                    setEnumToDb(key, tlocale, data);
                    return data;
                })
                .catch(() => {
                    return [];
                })
                .finally(async () => {
                    enumRequestManager.clear(key);

                });
            enumRequestManager.add(key, promise);
            return promise;
        },
        [updateId]
    );

    const clearEnum = useCallback(
        async (key?: string) => {
            if (key) {
                enumMemoryCache.clear(key);
                await clearEnumByKeyAndLocaleFromDb(key, tlocale);
            } else {
                enumMemoryCache.clearAll();
                await clearEnumsByLocaleFromDb(tlocale);
            }
            setUpdateId((prev) => prev + 1);
        },
        [tlocale]
    );

    const contextValue = useMemo(
        () => ({
            getEnum,
            clearEnum,
            updateId,
        }),
        [getEnum, clearEnum, updateId]
    );

    return (
        <EnumContext.Provider value={contextValue}>
            {children}
        </EnumContext.Provider>
    );
};

export const useEnumStore = () => {
    const ctx = useContext(EnumContext);
    if (!ctx) throw new Error("useEnumStore must be used inside EnumProvider");
    return ctx;
};
